'use client';

import { ReactNode } from 'react';
import * as Accordion from '@radix-ui/react-accordion';
import { AccordionHeader } from './AccordionHeader.use-client';
import { AccordionContent } from './AccordionContent';
import { AccordionItemSkeleton } from './AccordionItemSkeleton';

export interface AccordionData {
  title: string;
  image: string | ReactNode;
  fallbackImage?: string;
  subtitle?: string;
  totalAssets: number;
  ContentIndicatorSlot: ReactNode;
  onClick?: () => void;
}

export interface AccordionItem {
  value: string;
  header: AccordionData;
  contents: AccordionData[];
}

interface AccordionItemProps {
  internal: {
    className?: string;
    contentClassName?: string;
  };
  external: {
    item: AccordionItem;
    icon?: ReactNode;
    id?: string;
    loading?: boolean;
  };
}

export const Item = ({
  className,
  icon,
  item,
  id,
  loading,
}: AccordionItemProps['internal'] & AccordionItemProps['external']) => {
  const { value, header, contents } = item;

  const {
    ContentIndicatorSlot: HeaderIndicatorSlotComponent,
    ...restHeaderProps
  } = header;

  return (
    <Accordion.Item
      value={value}
      className={className}
      data-testid="accordion-item"
      {...(id ? { id } : {})}
    >
      <AccordionHeader icon={icon}>
        <AccordionContent {...restHeaderProps}>
          {HeaderIndicatorSlotComponent}
        </AccordionContent>
      </AccordionHeader>

      {loading ? (
        <Accordion.Content data-element="content" data-testid="content">
          {Array.from({ length: header.totalAssets }).map((_item, index) => {
            return <AccordionItemSkeleton key={index} />;
          })}
        </Accordion.Content>
      ) : (
        <Accordion.Content data-element="content" data-testid="content">
          {contents.map(({ ContentIndicatorSlot, ...restContent }) => (
            <AccordionContent
              {...restContent}
              key={`content_${restContent.title}_${restContent.subtitle}_${value}`}
            >
              {ContentIndicatorSlot}
            </AccordionContent>
          ))}
        </Accordion.Content>
      )}
    </Accordion.Item>
  );
};
